import React, { useState, useEffect } from 'react';
import { Card, Table, Spinner, Alert, Form, Button, Badge } from 'react-bootstrap';
import { fetchChangeLogs, clearChangeLogs, rollbackChangeLog } from '../http/changeLogAPI';

const ChangeLog = () => {
    const [changeLogs, setChangeLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({
        entity_type: '',
        action: '',
        limit: 100
    });

    const loadChangeLogs = async () => {
        setLoading(true);
        setError('');
        try {
            const params = {};
            if (filters.entity_type) params.entity_type = filters.entity_type;
            if (filters.action) params.action = filters.action;
            if (filters.limit) params.limit = filters.limit;
            
            const data = await fetchChangeLogs(params);
            setChangeLogs(Array.isArray(data.rows) ? data.rows : []);
        } catch (e) {
            setError('Ошибка при загрузке журнала изменений');
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadChangeLogs();
    }, []);

    const getActionBadge = (action) => {
        const variants = {
            create: 'success',
            update: 'primary',
            delete: 'danger'
        };
        const labels = {
            create: 'Создание',
            update: 'Обновление',
            delete: 'Удаление'
        };
        return <Badge bg={variants[action] || 'secondary'}>{labels[action] || action}</Badge>;
    };

    const getEntityTypeLabel = (type) => {
        const labels = {
            product: 'Товар',
            order: 'Заказ',
            user: 'Пользователь',
            category: 'Категория'
        };
        return labels[type] || type;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const parseChanges = (changesStr) => {
        if (!changesStr) return null;
        try {
            return JSON.parse(changesStr);
        } catch {
            return null;
        }
    };

    const handleClear = async () => {
        if (!window.confirm('Очистить весь журнал изменений?')) return;
        setLoading(true);
        setError('');
        try {
            await clearChangeLogs();
            setChangeLogs([]);
        } catch (e) {
            setError('Ошибка при очистке журнала');
        } finally {
            setLoading(false);
        }
    };

    const handleRollback = async (log) => {
        if (!window.confirm('Выполнить откат этого изменения?')) return;
        setLoading(true);
        setError('');
        try {
            await rollbackChangeLog(log.id);
            await loadChangeLogs();
        } catch (e) {
            setError(e?.response?.data?.message || 'Ошибка при откате');
        } finally {
            setLoading(false);
        }
    };

    const canRollback = (log) => {
        return log.action === 'update' && log.changes && ['product', 'order'].includes(log.entity_type);
    };

    return (
        <Card>
            <Card.Body>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <h3 className="mb-0">Журнал последних изменений</h3>
                    <div className="d-flex gap-2">
                        <Button variant="outline-primary" onClick={loadChangeLogs} disabled={loading}>
                            Обновить
                        </Button>
                        <Button variant="outline-danger" onClick={handleClear} disabled={loading}>
                            Очистить журнал
                        </Button>
                    </div>
                </div>

                {/* Фильтры */}
                <div className="mb-3 d-flex flex-column flex-md-row gap-2">
                    <div className="d-flex flex-column flex-md-row gap-2 w-100">
                        <Form.Select
                            className="w-100 w-md-auto"
                            value={filters.entity_type}
                            onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
                        >
                            <option value="">Все типы</option>
                            <option value="product">Товар</option>
                            <option value="order">Заказ</option>
                            <option value="user">Пользователь</option>
                        </Form.Select>
                        <Form.Select
                            className="w-100 w-md-auto"
                            value={filters.action}
                            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                        >
                            <option value="">Все действия</option>
                            <option value="create">Создание</option>
                            <option value="update">Обновление</option>
                            <option value="delete">Удаление</option>
                        </Form.Select>
                        <Form.Control
                            type="number"
                            placeholder="Лимит записей"
                            value={filters.limit}
                            onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value) || 100 })}
                            style={{ maxWidth: 160 }}
                        />
                    </div>
                    <div>
                        <Button variant="primary" onClick={loadChangeLogs} disabled={loading} className="w-100 w-md-auto">
                            Применить фильтры
                        </Button>
                    </div>
                </div>

                {error && <Alert variant="danger">{error}</Alert>}

                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="table-responsive d-none d-md-block">
                            <Table bordered hover size="sm">
                                <thead>
                                    <tr>
                                        <th>Дата и время</th>
                                        <th>Тип сущности</th>
                                        <th>Действие</th>
                                        <th>Описание</th>
                                        <th>Пользователь</th>
                                        <th>Детали изменений</th>
                                        <th>Действия</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {changeLogs.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center text-muted py-4">
                                                Нет записей в журнале
                                            </td>
                                        </tr>
                                    ) : (
                                        changeLogs.map((log) => {
                                            const changes = parseChanges(log.changes);
                                            return (
                                                <tr key={log.id}>
                                                    <td>{formatDate(log.created_at)}</td>
                                                    <td>{getEntityTypeLabel(log.entity_type)}</td>
                                                    <td>{getActionBadge(log.action)}</td>
                                                    <td>{log.description}</td>
                                                    <td>
                                                        {log.user?.name || log.user_name || 'Неизвестно'}
                                                        {log.user?.email && (
                                                            <div className="text-muted small">{log.user.email}</div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {changes ? (
                                                            <details>
                                                                <summary className="text-primary" style={{ cursor: 'pointer' }}>
                                                                    Показать детали
                                                                </summary>
                                                                <pre className="mt-2 small" style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                                                                    {JSON.stringify(changes, null, 2)}
                                                                </pre>
                                                            </details>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {canRollback(log) ? (
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => handleRollback(log)}
                                                                disabled={loading}
                                                            >
                                                                Откатить
                                                            </Button>
                                                        ) : (
                                                            <span className="text-muted small">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {/* Mobile cards */}
                        <div className="d-block d-md-none">
                            {changeLogs.length === 0 ? (
                                <div className="text-center text-muted py-3">Нет записей в журнале</div>
                            ) : (
                                changeLogs.map((log) => {
                                    const changes = parseChanges(log.changes);
                                    return (
                                        <Card key={log.id} className="mb-2">
                                            <Card.Body>
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <div className="small text-muted">{formatDate(log.created_at)}</div>
                                                    {getActionBadge(log.action)}
                                                </div>
                                                <div className="fw-bold mb-1">{getEntityTypeLabel(log.entity_type)}</div>
                                                <div className="mb-1">{log.description}</div>
                                                <div className="small text-muted mb-2">
                                                    {log.user?.name || log.user_name || 'Неизвестно'}
                                                    {log.user?.email && ` • ${log.user.email}`}
                                                </div>
                                                {changes ? (
                                                    <details>
                                                        <summary className="text-primary" style={{ cursor: 'pointer' }}>
                                                            Показать детали
                                                        </summary>
                                                        <pre className="mt-2 small" style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                                                            {JSON.stringify(changes, null, 2)}
                                                        </pre>
                                                    </details>
                                                ) : (
                                                    <span className="text-muted small">Детали отсутствуют</span>
                                                )}
                                                {canRollback(log) && (
                                                    <div className="mt-2">
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handleRollback(log)}
                                                            disabled={loading}
                                                        >
                                                            Откатить
                                                        </Button>
                                                    </div>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    );
                                })
                            )}
                        </div>
                    </>
                )}
            </Card.Body>
        </Card>
    );
};

export default ChangeLog;
